import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { socketService } from '../services/socket';
import { useSnackbar } from 'notistack';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  useTheme,
  alpha,
  Stack,
  Avatar,
  AvatarGroup,
  Badge,
  Collapse,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Send,
  Schedule,
  Save,
  NotificationImportant,
  Palette,
  Timer,
  Groups,
  Person,
  Refresh,
  Delete,
  Edit,
  ExpandMore,
  ExpandLess,
  InfoOutlined,
  WarningAmberOutlined,
  ErrorOutlineOutlined,
  CheckCircleOutlined,
  Campaign,
  PhoneAndroid,
  Computer,
  Tablet,
  Preview,
  FormatColorText,
  FormatColorFill,
  Link as LinkIcon,
  AccessTime,
  CalendarToday,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PageHeader } from '../components/ui/PageHeader';
import { GlassCard } from '../components/ui/GlassCard';
import { format } from 'date-fns';
import { userService } from '../services/user';
import { AgentUser } from '../types';

interface PushNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  priority: 'low' | 'normal' | 'high' | 'critical';
  backgroundColor: string;
  textColor: string;
  displayDuration: number; // seconds
  targetAudience: 'all' | 'group' | 'individual';
  targetUsers: string[];
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  sentAt?: Date;
  scheduledFor?: Date;
  deliveryCount?: number;
  readCount?: number;
}

const notificationIcons = {
  info: <InfoOutlined />,
  warning: <WarningAmberOutlined />,
  error: <ErrorOutlineOutlined />,
  success: <CheckCircleOutlined />,
};

const priorityColors = {
  low: '#10B981',
  normal: '#3B82F6',
  high: '#F59E0B',
  critical: '#EF4444',
};

const PushNotifications: React.FC = () => {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [devicePreview, setDevicePreview] = useState<'phone' | 'tablet' | 'desktop'>('phone');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [notifications, setNotifications] = useState<PushNotification[]>([]);
  const [editingNotification, setEditingNotification] = useState<PushNotification | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<AgentUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    link: '',
    icon: 'info' as 'info' | 'warning' | 'error' | 'success',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'critical',
    bgColor: '#2563eb', // Nice blue instead of branding color
    textColor: '#ffffff',
    displayMode: 'toast' as 'toast' | 'dialog',
    scheduledTime: null as Date | null,
    expiresIn: 24, // hours
  });

  // Groups with 0 counts as we don't have group data yet
  const availableGroups = [
    { id: 'all-users', name: 'All Users', count: 0 },
    { id: 'administrators', name: 'Administrators', count: 0 },
    { id: 'support-staff', name: 'Support Staff', count: 0 },
    { id: 'developers', name: 'Developers', count: 0 },
    { id: 'managers', name: 'Managers', count: 0 },
  ];

  // Calculate relative luminance to determine if text should be light or dark
  const getLuminance = (color: string): number => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;
    
    // Calculate relative luminance
    const sRGB = [r, g, b].map(val => {
      if (val <= 0.03928) return val / 12.92;
      return Math.pow((val + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
  };

  // Automatically adjust text color based on background luminance
  const getContrastTextColor = (bgColor: string): string => {
    try {
      const luminance = getLuminance(bgColor);
      // Use white text for dark backgrounds, black for light backgrounds
      return luminance > 0.5 ? '#000000' : '#ffffff';
    } catch {
      return '#ffffff'; // Default to white if parsing fails
    }
  };

  const handleSendNotification = async (action: 'send' | 'schedule' | 'draft') => {
    if (!formData.title || !formData.message) {
      enqueueSnackbar('Please fill in title and message', { variant: 'error' });
      return;
    }

    if (selectedUsers.length === 0 && selectedGroups.length === 0) {
      enqueueSnackbar('Please select at least one recipient', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    
    try {
      const socket = socketService.getSocket();
      if (!socket) {
        enqueueSnackbar('Socket not connected', { variant: 'error' });
        setSubmitting(false);
        return;
      }

      // Combine selected users and groups into destinations array
      const destinations = [
        ...selectedUsers,
        ...selectedGroups.map(g => `group:${g}`),
      ];

      socket.emit('sendPushMessage', {
        title: formData.title,
        body: formData.message,
        destinations,
        link: formData.link,
        displayMode: formData.displayMode,
        displayColor: formData.bgColor,
        sendOn: action === 'schedule' && formData.scheduledTime ? 'scheduled' : 'immediate',
        sendDate: formData.scheduledTime?.toISOString(),
        lifeTime: formData.expiresIn * 3600, // Convert hours to seconds
        priority: formData.priority,
      }, (response: any) => {
        if (response.success) {
          enqueueSnackbar(
            action === 'send' 
              ? `Notification sent to ${response.queuedCount} recipients`
              : action === 'schedule' 
                ? 'Notification scheduled successfully'
                : 'Draft saved successfully',
            { variant: 'success' }
          );
          
          // Reset form after successful send
          if (action === 'send') {
            setFormData({
              title: '',
              message: '',
              link: '',
              icon: 'info',
              priority: 'normal',
              bgColor: '#2563eb', // Keep consistent blue default
              textColor: '#ffffff',
              displayMode: 'toast',
              scheduledTime: null,
              expiresIn: 24,
            });
            setSelectedGroups([]);
            setSelectedUsers([]);
          }
        } else {
          enqueueSnackbar(response.error || 'Failed to send notification', { variant: 'error' });
        }
        setSubmitting(false);
      });
    } catch (error) {
      console.error('Failed to send notification:', error);
      enqueueSnackbar('Failed to send notification', { variant: 'error' });
      setSubmitting(false);
    }
  };

  // Throttle color updates to prevent CPU hogging
  const colorUpdateTimeoutRef = useRef<NodeJS.Timeout>();
  
  const handleColorChange = useCallback((type: 'bg' | 'text', color: string) => {
    // Clear any pending updates
    if (colorUpdateTimeoutRef.current) {
      clearTimeout(colorUpdateTimeoutRef.current);
    }
    
    // Throttle the update to max once per 50ms
    colorUpdateTimeoutRef.current = setTimeout(() => {
      if (type === 'bg') {
        // When background changes, automatically adjust text color for contrast
        const textColor = getContrastTextColor(color);
        setFormData(prev => ({
          ...prev,
          bgColor: color,
          textColor: textColor,
        }));
      } else {
        // Manual text color change
        setFormData(prev => ({
          ...prev,
          textColor: color,
        }));
      }
    }, 50);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (colorUpdateTimeoutRef.current) {
        clearTimeout(colorUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Removed automatic theme color update to keep user's color choice

  // Load users on mount
  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const users = await userService.getUserList();
        setAvailableUsers(users);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    // Load push templates on mount
    socket.emit('getPushTemplates', (response: any) => {
      if (response.success && response.templates) {
        console.log('Loaded push templates:', response.templates);
        // Could set templates to state here if needed
      }
    });

    // Listen for push message updates
    socket.on('pushMessageDelivered', (data: any) => {
      console.log('Push message delivered:', data);
    });

    socket.on('pushMessageRead', (data: any) => {
      console.log('Push message read:', data);
    });

    return () => {
      socket.off('pushMessageDelivered');
      socket.off('pushMessageRead');
    };
  }, []);

  const NotificationPreview = () => {
    if (formData.displayMode === 'dialog') {
      // Dialog/Modal Preview
      return (
        <Box sx={{ 
          position: 'relative', 
          width: '100%',
          maxWidth: devicePreview === 'phone' ? 320 : devicePreview === 'tablet' ? 480 : 600,
          mx: 'auto'
        }}>
          {/* Backdrop */}
          <Box sx={{ 
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            borderRadius: 2,
            zIndex: 1
          }} />
          
          {/* Dialog */}
          <Card
            sx={{
              position: 'relative',
              zIndex: 2,
              backgroundColor: formData.bgColor,
              color: formData.textColor,
              boxShadow: theme.shadows[24],
              transition: 'all 0.3s ease',
              transform: 'scale(0.9)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box sx={{ 
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: alpha(priorityColors[formData.priority], 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  {React.cloneElement(notificationIcons[formData.icon], { 
                    sx: { fontSize: 32, color: priorityColors[formData.priority] } 
                  })}
                </Box>
                <Typography variant="h6" fontWeight={700}>
                  {formData.title || 'Important Notification'}
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ textAlign: 'center', mb: 3, opacity: 0.9 }}>
                {formData.message || 'Your important message will appear here. This is a dialog-style notification that requires user interaction.'}
              </Typography>
              
              {formData.link && (
                <Box sx={{ 
                  p: 1.5, 
                  backgroundColor: alpha(formData.textColor, 0.05),
                  borderRadius: 1,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <LinkIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                    {formData.link}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  fullWidth
                  sx={{ borderColor: alpha(formData.textColor, 0.3) }}
                >
                  Dismiss
                </Button>
                <Button 
                  variant="contained" 
                  fullWidth
                  sx={{ 
                    backgroundColor: priorityColors[formData.priority],
                    '&:hover': {
                      backgroundColor: alpha(priorityColors[formData.priority], 0.8)
                    }
                  }}
                >
                  View Details
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }
    
    // Toast Preview (existing code)
    return (
      <Card
        sx={{
          backgroundColor: formData.bgColor,
          color: formData.textColor,
          maxWidth: devicePreview === 'phone' ? 320 : devicePreview === 'tablet' ? 480 : 600,
          mx: 'auto',
          position: 'relative',
          overflow: 'visible',
          boxShadow: theme.shadows[10],
          transition: 'all 0.3s ease',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 1, 
              backgroundColor: alpha(formData.textColor, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {notificationIcons[formData.icon]}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {formData.title || 'Notification Title'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                {formData.message || 'Your notification message will appear here...'}
              </Typography>
              {formData.link && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.7 }}>
                  <LinkIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                  {formData.link}
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Priority Badge */}
          <Chip
            label={formData.priority.toUpperCase()}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: priorityColors[formData.priority],
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.65rem',
            }}
          />
        </CardContent>
      </Card>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <PageHeader
          title="Push Notifications"
          subtitle="Send targeted notifications to users and groups"
          breadcrumbs={[
            { label: 'Home', href: '/dashboard' },
            { label: 'Notifications', href: '/notifications' },
            { label: 'Push Notifications' },
          ]}
        />

        <Grid container spacing={3}>
          {/* Left Panel - Notification Form */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Stack spacing={3}>
              {/* Notification Content */}
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Notification Content
                </Typography>
                
                <Stack spacing={3}>
                  <TextField
                    label="Title"
                    fullWidth
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter notification title..."
                  />
                  
                  <TextField
                    label="Message"
                    fullWidth
                    required
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Enter your notification message..."
                  />
                  
                  <TextField
                    label="Link (Optional)"
                    fullWidth
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="https://example.com"
                    InputProps={{
                      startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                  
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <FormControl fullWidth>
                        <InputLabel>Icon</InputLabel>
                        <Select
                          value={formData.icon}
                          onChange={(e) => setFormData({ ...formData, icon: e.target.value as any })}
                          label="Icon"
                        >
                          {Object.entries(notificationIcons).map(([key, icon]) => (
                            <MenuItem key={key} value={key}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {icon}
                                <Typography>{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid size={6}>
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={formData.priority}
                          onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                          label="Priority"
                        >
                          {Object.entries(priorityColors).map(([key, color]) => (
                            <MenuItem key={key} value={key}>
                              <Chip
                                label={key.toUpperCase()}
                                size="small"
                                sx={{
                                  backgroundColor: color,
                                  color: '#fff',
                                  fontWeight: 600,
                                }}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Stack>
              </GlassCard>

              {/* Display Settings */}
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Display Settings
                </Typography>
                
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Display Mode
                    </Typography>
                    <ToggleButtonGroup
                      value={formData.displayMode}
                      exclusive
                      onChange={(e, value) => value && setFormData({ ...formData, displayMode: value })}
                      fullWidth
                    >
                      <ToggleButton value="toast">
                        <NotificationImportant sx={{ mr: 1 }} />
                        Toast
                      </ToggleButton>
                      <ToggleButton value="dialog">
                        <Campaign sx={{ mr: 1 }} />
                        Dialog
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <FormatColorFill sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          Background Color
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              backgroundColor: formData.bgColor,
                              border: `2px solid ${theme.palette.divider}`,
                              cursor: 'pointer',
                            }}
                          />
                          <input
                            type="color"
                            value={formData.bgColor}
                            onChange={(e) => handleColorChange('bg', e.target.value)}
                            style={{ 
                              width: '100%',
                              height: 40,
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    
                    <Grid size={6}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <FormatColorText sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          Text Color
                          <Chip 
                            label="Auto" 
                            size="small" 
                            sx={{ 
                              ml: 1, 
                              height: 18,
                              fontSize: '0.7rem',
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }} 
                          />
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              backgroundColor: formData.textColor,
                              border: `2px solid ${theme.palette.divider}`,
                              cursor: 'pointer',
                            }}
                          />
                          <input
                            type="color"
                            value={formData.textColor}
                            onChange={(e) => handleColorChange('text', e.target.value)}
                            style={{ 
                              width: '100%',
                              height: 40,
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Expires In ({formData.expiresIn} hours)
                    </Typography>
                    <Slider
                      value={formData.expiresIn}
                      onChange={(e, value) => setFormData({ ...formData, expiresIn: value as number })}
                      min={1}
                      max={72}
                      marks={[
                        { value: 1, label: '1h' },
                        { value: 24, label: '24h' },
                        { value: 48, label: '48h' },
                        { value: 72, label: '72h' },
                      ]}
                    />
                  </Box>
                </Stack>
              </GlassCard>

              {/* Target Audience */}
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Target Audience
                </Typography>
                
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                      Select Groups
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {availableGroups.map((group) => (
                        <Chip
                          key={group.id}
                          label={`${group.name} (${group.count})`}
                          onClick={() => {
                            setSelectedGroups(prev =>
                              prev.includes(group.id)
                                ? prev.filter(id => id !== group.id)
                                : [...prev, group.id]
                            );
                          }}
                          color={selectedGroups.includes(group.id) ? 'primary' : 'default'}
                          variant={selectedGroups.includes(group.id) ? 'filled' : 'outlined'}
                          icon={<Groups />}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                      Select Individual Users
                    </Typography>
                    <Autocomplete
                      multiple
                      loading={loadingUsers}
                      options={availableUsers}
                      getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                      value={availableUsers.filter(u => selectedUsers.includes(u.agentUserId || u.userId))}
                      onChange={(e, value) => {
                        setSelectedUsers(value.map(v => v.agentUserId || v.userId));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and select users..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                            {option.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{option.fullName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email} • {option.agentName}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />
                  </Box>
                </Stack>
              </GlassCard>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => handleSendNotification('draft')}
                  disabled={submitting}
                >
                  Save Draft
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Schedule />}
                  onClick={() => handleSendNotification('schedule')}
                  disabled={submitting}
                >
                  Schedule
                </Button>
                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
                  onClick={() => handleSendNotification('send')}
                  disabled={submitting || !formData.title || !formData.message}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  }}
                >
                  {submitting ? 'Sending...' : 'Send Now'}
                </Button>
              </Box>
            </Stack>
          </Grid>

          {/* Right Panel - Preview */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Stack spacing={3} sx={{ position: 'sticky', top: 20 }}>
              <GlassCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Live Preview
                  </Typography>
                  <ToggleButtonGroup
                    value={devicePreview}
                    exclusive
                    onChange={(e, value) => value && setDevicePreview(value)}
                    size="small"
                  >
                    <ToggleButton value="phone">
                      <PhoneAndroid />
                    </ToggleButton>
                    <ToggleButton value="tablet">
                      <Tablet />
                    </ToggleButton>
                    <ToggleButton value="desktop">
                      <Computer />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  minHeight: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <NotificationPreview />
                </Box>
              </GlassCard>

              {/* Summary */}
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Summary
                </Typography>
                
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Recipients
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedGroups.reduce((acc, groupId) => {
                        const group = availableGroups.find(g => g.id === groupId);
                        return acc + (group?.count || 0);
                      }, 0) + selectedUsers.length} users
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Priority
                    </Typography>
                    <Chip
                      label={formData.priority.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: priorityColors[formData.priority],
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Display Mode
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formData.displayMode.charAt(0).toUpperCase() + formData.displayMode.slice(1)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Expires In
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formData.expiresIn} hours
                    </Typography>
                  </Box>
                  
                  {formData.scheduledTime && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Scheduled For
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {format(formData.scheduledTime, 'PPp')}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </GlassCard>

              {/* Schedule Settings */}
              <GlassCard>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Schedule Settings
                </Typography>
                
                <DateTimePicker
                  label="Schedule for later (optional)"
                  value={formData.scheduledTime}
                  onChange={(value) => setFormData({ ...formData, scheduledTime: value })}
                  open={datePickerOpen}
                  onOpen={() => setDatePickerOpen(true)}
                  onClose={() => setDatePickerOpen(false)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: formData.scheduledTime 
                        ? `Will be sent on ${format(formData.scheduledTime, 'PPpp')}`
                        : 'Leave empty to send immediately',
                      onFocus: () => setDatePickerOpen(true),
                      onClick: () => setDatePickerOpen(true),
                    },
                  }}
                />
              </GlassCard>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </LocalizationProvider>
  );
};

export default PushNotifications;